import { useCallback } from 'react';

import { MailIcon, PlusIcon, SettingsIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Routes, Route, Navigate, useLocation, NavLink, useMatch } from 'react-router';
import { useTitle } from 'react-use';

import { ReadOnlyBanner } from '@/components/atoms/ReadOnlyBanner/ReadOnlyBanner';
import { MODAL_MEMBER_INVITE, MODAL_TEAM } from '@/components/organisms/Modals/ModalConductor';
import { SettingsNavigation } from '@/components/organisms/Navbar/SettingsNavigation/SettingsNavigation';
import {
  PageSubHeader,
  PageSubHeaderBreadcrumbs,
  PageSubHeaderTabs,
  PageSubHeaderActions,
} from '@/components/organisms/PageSubHeader';
import { Sidebar } from '@/components/organisms/Sidebar/Sidebar';
import { SidebarLayout } from '@/components/organisms/Sidebar/SidebarLayout';
import { CreateRolePopover } from '@/components/pages/ManageAccess/CreateRolePopover';
import { ManageAccessMembers } from '@/components/pages/ManageAccess/ManageAccessMembers';
import { ManageAccessRoles } from '@/components/pages/ManageAccess/ManageAccessRoles';
import { RolePermissionsEditor } from '@/components/pages/ManageAccess/RoleEditor/RolePermissionsEditor';
import { RoleEditorBreadcrumb } from '@/components/pages/ManageAccess/RoleEditorBreadcrumb';
import {
  RoleEditorSaveButton,
  RoleEditorSaveProvider,
} from '@/components/pages/ManageAccess/RoleEditorSaveAction';
import { RolePermissionsPanel } from '@/components/pages/ManageAccess/RolePermissionsPanel';
import NotFound from '@/components/pages/NotFound/NotFound';
import { Teams } from '@/components/pages/Teams/Teams';
import Layout from '@/components/templates/Layout';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import PermissionsWrapper from '@/components/templates/PermissionsWrapper';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useTabEmptyState } from '@/hooks/useTabEmptyState';
import { pushModal } from '@/redux/modals/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import { trackEvent } from '@/segment/segment';
import { actions } from '@/shared/types/permissions';
import { EventName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';

const ROLE_EDITOR_ROUTE = '/:slug/account/manage/roles/:role_id';

const getActiveTab = (pathname: string) => {
  if (pathname.includes('team-access')) {
    return 'teams';
  }
  if (pathname.includes('roles')) {
    return 'roles';
  }
  return 'members';
};

const ManageRouterContent: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const slug = useSelector(selectAccountSlug);
  const activeTab = getActiveTab(location.pathname);
  const isTeamsPage = activeTab === 'teams';
  const isRolesPage = activeTab === 'roles';

  const roleEditorMatch = useMatch(ROLE_EDITOR_ROUTE);

  useTitle(t('permissions.manage_access'));

  const canAssign = useSelector((state: RootState) =>
    getPermissions(state, 'roles', actions.ASSIGN)
  );

  const canWriteTeams = useSelector((state: RootState) =>
    getPermissions(state, 'teams', actions.WRITE)
  );

  const canWriteRoles = useSelector((state: RootState) =>
    getPermissions(state, 'roles', actions.WRITE)
  );

  const canReadRoles = useSelector((state: RootState) =>
    getPermissions(state, 'roles', actions.READ)
  );

  const canReadTeams = useSelector((state: RootState) =>
    getPermissions(state, 'teams', actions.READ)
  );

  const canInvite = useSelector((state: RootState) =>
    getPermissions(state, 'invitations', actions.WRITE)
  );

  const editedRoleId = roleEditorMatch?.params.role_id;

  const [isTeamsEmpty, setIsTeamsEmpty] = useTabEmptyState(activeTab);

  const handleClick = useCallback(() => {
    if (!isTeamsPage) {
      dispatch(pushModal(MODAL_MEMBER_INVITE));
    } else {
      trackEvent(EventName.ClickCreateAccessTeam);
      dispatch(pushModal(MODAL_TEAM));
    }
  }, [dispatch, isTeamsPage]);

  const primaryText = !isTeamsPage ? t('members.invite') : t('teams.create_team');
  const canWriteTab = () => {
    if (isRolesPage) {
      return canWriteRoles;
    }
    if (isTeamsPage) {
      return canWriteTeams;
    }
    // The members tab both assigns roles and sends invitations, so either one is enough.
    return canAssign || canInvite;
  };
  const canReadTab = () => {
    if (isRolesPage) {
      return canReadRoles;
    }
    if (isTeamsPage) {
      return canReadTeams;
    }
    return true;
  };
  const isReadOnly = editedRoleId ? canReadRoles && !canWriteRoles : canReadTab() && !canWriteTab();

  const renderBreadcrumbs = () => {
    if (editedRoleId) {
      return <RoleEditorBreadcrumb roleId={editedRoleId} />;
    }

    return (
      <PageSubHeaderBreadcrumbs>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbPage>
                <div className="flex h-8 w-8 items-center justify-center">
                  <SettingsIcon size={16} className="text-muted-foreground" />
                </div>
                <div className="mr-4 ml-2 h-4 w-px bg-border" />
                <span className="text-sm leading-5 font-normal">
                  {t('permissions.manage_access')}
                </span>
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </PageSubHeaderBreadcrumbs>
    );
  };

  const renderActions = () => {
    if (editedRoleId) {
      return (
        <PageSubHeaderActions>
          <RoleEditorSaveButton />
        </PageSubHeaderActions>
      );
    }
    if (isRolesPage) {
      return (
        <PageSubHeaderActions>
          <CreateRolePopover disabled={!canWriteRoles} />
        </PageSubHeaderActions>
      );
    }

    if (isTeamsPage && isTeamsEmpty && canWriteTeams) {
      return null;
    }

    return (
      <PageSubHeaderActions>
        <Button
          disabled={isTeamsPage ? !canWriteTeams : !canInvite}
          onClick={handleClick}
          size="sm"
        >
          {isTeamsPage ? <PlusIcon size="16" /> : <MailIcon size="16" />}
          {primaryText}
        </Button>
      </PageSubHeaderActions>
    );
  };

  return (
    <Layout>
      <SidebarLayout
        autoSaveId="manage-access-sidebar"
        sidebar={
          <Sidebar when="role_permissions" title={t('roles.view_permissions')}>
            <RolePermissionsPanel />
          </Sidebar>
        }
      >
        <PageSubHeader
          inlineTabs
          breadcrumbs={renderBreadcrumbs()}
          tabs={
            editedRoleId ? undefined : (
              <PageSubHeaderTabs>
                <Tabs value={activeTab} className="w-auto">
                  <TabsList>
                    <TabsTrigger value="members" asChild>
                      <NavLink to={`/${slug}/account/manage/member-access`}>
                        {t('common.members')}
                      </NavLink>
                    </TabsTrigger>
                    <TabsTrigger value="teams" asChild>
                      <NavLink to={`/${slug}/account/manage/team-access`}>
                        {t('common.teams')}
                      </NavLink>
                    </TabsTrigger>
                    <TabsTrigger value="roles" asChild>
                      <NavLink to={`/${slug}/account/manage/roles`}>
                        {t('common.members_roles')}
                      </NavLink>
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </PageSubHeaderTabs>
            )
          }
          actions={renderActions()}
        />
        {isReadOnly && <ReadOnlyBanner />}
        <div className="flex min-h-0 flex-1">
          <SettingsNavigation />
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <PageContentWrapper className="p-0">
              <Routes>
                <Route path="/" element={<Navigate to="member-access" replace />} />
                <Route path="/member-access" element={<ManageAccessMembers />} />
                <Route
                  path="/team-access"
                  element={
                    <PermissionsWrapper module="teams">
                      <Teams onEmptyStateChange={setIsTeamsEmpty} />
                    </PermissionsWrapper>
                  }
                />
                <Route
                  path="/roles"
                  element={
                    <PermissionsWrapper module="roles">
                      <ManageAccessRoles />
                    </PermissionsWrapper>
                  }
                />
                <Route
                  path="/roles/:role_id"
                  element={
                    <PermissionsWrapper module="roles">
                      <RolePermissionsEditor />
                    </PermissionsWrapper>
                  }
                />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </PageContentWrapper>
          </div>
        </div>
      </SidebarLayout>
    </Layout>
  );
};

const ManageRouter: React.FC = () => (
  <RoleEditorSaveProvider>
    <ManageRouterContent />
  </RoleEditorSaveProvider>
);

export default ManageRouter;
